import type { FormConfig, FormRule, RuleAction, RuleWhen } from '../shared/types';
import { normalizeAddressValue, normalizeNameValue } from '../shared/address';
import { NO_SCORE, ScoreTotals, scorePercent } from './scoring';

/**
 * The rule engine behind the Logic page. fieldVisible / fieldRequired in
 * config.ts stay the authority for flag-based visibility; these rules run on
 * top for VALUE-based visibility, requiredness, prefill and calculations. The
 * Form Builder's Test mode and the public form share these functions so the
 * preview cannot lie.
 *
 * A rule is ONE shape: a trigger — a condition group (match all / any), or
 * 'always' — plus a LIST of actions. One trigger can therefore show some
 * fields, hide others, make one required, prefill more and set a total.
 */

/**
 * Compare as text: answers arrive from the DOM as strings, while automatic
 * counts and checkbox values are numbers/booleans, so `2` must match "2".
 */
const asText = (v: unknown) => (v === undefined || v === null ? '' : String(v));

/**
 * The answer as the LIST it might be.
 *
 * A question that takes more than one answer stores an array; every other
 * question stores one value. Reading the single value as a one-item list is
 * what lets `includes` mean the same thing on both, so a rule keeps working
 * when a question is changed from one kind to the other. An answer that is
 * not there is an empty list, never a list holding nothing — the same
 * asymmetry `isnot` has, and for the same reason.
 */
const asList = (v: unknown): unknown[] =>
  (Array.isArray(v) ? v : v === undefined || v === null ? [] : [v]);

/**
 * Read a value by key, or by a dotted path into it: 'shipAddress.company' (a
 * composite answer, one dot deep, the way Name/Address are stored) or
 * 'parent.shipAddress.company' (the same answer on the submission this one
 * continues — mirrors server/src/lib/conditions.ts).
 */
export function valueAt(values: Record<string, unknown>, path: string): unknown {
  const i = path.indexOf('.');
  if (i < 0) return values[path];
  const head = values[path.slice(0, i)];
  return head && typeof head === 'object' && !Array.isArray(head)
    ? valueAt(head as Record<string, unknown>, path.slice(i + 1))
    : undefined;
}

/** How a composite field renormalizes after a rule writes one of its parts. */
export type CompositeKinds = Record<string, 'name' | 'address' | 'shipping'>;

/**
 * The answer as a NUMBER, or NaN when it is not one. A number arrives as
 * itself from a scale or a count and as text from an input; a blank, a list,
 * an object or a boolean is not a number, whatever `Number()` would make of it.
 */
const asNumber = (v: unknown): number => {
  if (typeof v === 'number') return v;
  if (typeof v === 'string' && v.trim() !== '') return Number(v);
  return NaN;
};

/**
 * The answer as a DATE — `YYYY-MM-DD` plus, when the answer carries one,
 * `HH:mm`. A date-time answer's offset is dropped: the rule reads the clock
 * the respondent saw. Anything that does not start like an ISO date is null.
 */
const DATE_PART = /^(\d{4}-\d{2}-\d{2})(?:[T ](\d{2}:\d{2}))?/;
const asDate = (v: unknown): { day: string; time?: string } | null => {
  if (typeof v !== 'string') return null;
  const m = DATE_PART.exec(v.trim());
  return m ? { day: m[1], ...(m[2] ? { time: m[2] } : {}) } : null;
};
/** -1, 0 or 1 — or null when either side is not a date. Two times compare to the minute; otherwise the days. */
const compareDates = (a: unknown, b: unknown): number | null => {
  const x = asDate(a);
  const y = asDate(b);
  if (!x || !y) return null;
  const [l, r] = x.time && y.time ? [`${x.day}T${x.time}`, `${y.day}T${y.time}`] : [x.day, y.day];
  return l < r ? -1 : l > r ? 1 : 0;
};

export function evalWhen(when: RuleWhen, values: Record<string, unknown>): boolean {
  const v = valueAt(values, when.field);
  if (when.op === 'gt' || when.op === 'lt' || when.op === 'gte' || when.op === 'lte') {
    const a = asNumber(v);
    const b = asNumber(when.value);
    if (Number.isNaN(a) || Number.isNaN(b)) return false;
    return when.op === 'gt' ? a > b : when.op === 'lt' ? a < b : when.op === 'gte' ? a >= b : a <= b;
  }
  if (when.op === 'before' || when.op === 'after') {
    const c = compareDates(v, when.value);
    return c !== null && (when.op === 'before' ? c < 0 : c > 0);
  }
  if (when.op === 'contains') {
    const needle = asText(when.value).toLowerCase();
    if (!needle) return false;
    if (Array.isArray(v)) return v.some((x) => asText(x).toLowerCase() === needle);
    if (v && typeof v === 'object') return false;
    return asText(v).toLowerCase().includes(needle);
  }
  if (when.op === 'is') return asText(v) === asText(when.value);
  if (when.op === 'isnot') return v !== undefined && asText(v) !== asText(when.value);
  // Whole values, never substrings: an option is ticked or it is not.
  if (when.op === 'includes') return asList(v).some((x) => asText(x) === asText(when.value));
  // The same asymmetry `isnot` has, and for the same reason: a question
  // nobody was asked does not "not include" anything. An answered question
  // holding an empty list DOES — they were asked, and ticked nothing.
  if (when.op === 'notincludes') {
    return v !== undefined && v !== null && !asList(v).some((x) => asText(x) === asText(when.value));
  }
  if (when.op === 'filled') return !!v;
  if (when.op === 'empty') return !v;
  return false;
}

/**
 * Does a rule run right now?
 *
 * `trigger: 'always'` always does — that is what every upgraded calculation
 * carries, and it reproduces exactly what calculations did before. Otherwise
 * the condition group has to hold, and an EMPTY group never holds: a rule with
 * no condition is an unfinished rule, not one that applies to everything. The
 * Logic page now warns about that state out loud instead of hiding it.
 */
export function ruleFires(rule: FormRule, values: Record<string, unknown>): boolean {
  if (rule.trigger === 'always') return true;
  const conds = rule.when || [];
  if (!conds.length) return false;
  return rule.match === 'any'
    ? conds.some((c) => evalWhen(c, values))
    : conds.every((c) => evalWhen(c, values));
}

/**
 * Does a bare condition group hold? The same reading `ruleFires` gives a
 * `when` rule — an EMPTY group holds for nothing — for the places that carry
 * a group without being a rule: a container's conditional counts. Mirrors
 * `groupHolds` in server/src/lib/conditions.ts.
 */
export function groupHolds(match: 'all' | 'any', when: RuleWhen[], values: Record<string, unknown>): boolean {
  const conds = when || [];
  if (!conds.length) return false;
  return match === 'any' ? conds.some((c) => evalWhen(c, values)) : conds.every((c) => evalWhen(c, values));
}

/**
 * Field keys hidden by the current answers.
 *
 * SHOW is permissive and counted per target, not per rule: a target that any
 * `show` action names is hidden only while NONE of the rules naming it fires.
 * Two rules revealing the same field on different answers — "show it when yes"
 * alongside "show it when no" — is the ordinary way to say "show it once this
 * is answered", and reading each rule alone would let whichever one happened
 * not to fire veto the one that did, leaving the field hidden either way.
 *
 * HIDE stays a veto: a firing `hide` takes the field whatever else says, which
 * is the safe reading of an author who asked for it to be gone.
 */
export function ruleHiddenKeys(rules: FormRule[], values: Record<string, unknown>, cfg: FormConfig): Set<string> {
  void cfg;
  /** every target a `show` names -> did any rule naming it fire? */
  const revealed = new Map<string, boolean>();
  const hidden = new Set<string>();
  for (const r of rules) {
    const fires = ruleFires(r, values);
    for (const a of r.actions || []) {
      if (a.type === 'show') a.targets.forEach((t) => revealed.set(t, revealed.get(t) || fires));
      if (a.type === 'hide' && fires) a.targets.forEach((t) => hidden.add(t));
    }
  }
  for (const [target, fired] of revealed) if (!fired) hidden.add(target);
  return hidden;
}

/** Every field key a show/hide action targets (those keys skip legacy showWhen). */
export function ruleTargetKeys(rules: FormRule[]): Set<string> {
  const keys = new Set<string>();
  for (const r of rules) {
    for (const a of r.actions || []) {
      if (a.type === 'show' || a.type === 'hide') a.targets.forEach((t) => keys.add(t));
    }
  }
  return keys;
}

/**
 * Keys targeted by a SHOW action specifically. A builder-hidden field in this
 * set becomes rule-driven: it renders whenever the show rule fires.
 */
export function ruleShowTargetKeys(rules: FormRule[]): Set<string> {
  const keys = new Set<string>();
  for (const r of rules) {
    for (const a of r.actions || []) {
      if (a.type === 'show') a.targets.forEach((t) => keys.add(t));
    }
  }
  return keys;
}

/**
 * Targets a firing rule makes REQUIRED / OPTIONAL right now.
 *
 * These are the raw targets, containers included ('sec:' / 'card:' / 'wnode:');
 * evaluate.ts expands a container into the fields inside it in exactly the pass
 * that already expands a hidden one, so a card made required demands every
 * question in it and a card made optional waives every one.
 *
 * Both are unions across firing rules, not per-rule verdicts: two rules can
 * reach the same field on different answers, and it is required (or waived) as
 * soon as either of them holds.
 */
const firingTargets = (
  rules: FormRule[], values: Record<string, unknown>, type: 'require' | 'optional' | 'lock' | 'unlock'
): Set<string> => {
  const keys = new Set<string>();
  for (const r of rules) {
    if (!ruleFires(r, values)) continue;
    for (const a of r.actions || []) if (a.type === type) a.targets.forEach((t) => keys.add(t));
  }
  return keys;
};
export const ruleRequiredKeys = (rules: FormRule[], values: Record<string, unknown>) =>
  firingTargets(rules, values, 'require');
export const ruleOptionalKeys = (rules: FormRule[], values: Record<string, unknown>) =>
  firingTargets(rules, values, 'optional');
/**
 * Targets a firing `lock` action makes read-only right now.
 *
 * A lock says nothing about the VALUE — it keeps whatever answer is there and
 * takes away the ability to change it. That is the difference from a locked
 * prefill, which is a mirror: it writes the source's value on every pass and
 * disables the input as a consequence. Locking a field by prefilling it from
 * itself was the only way to say this before, and it said it by accident.
 */
export const ruleLockedKeys = (rules: FormRule[], values: Record<string, unknown>) =>
  firingTargets(rules, values, 'lock');
/**
 * Targets a firing `unlock` action hands back to the respondent.
 *
 * Beats `lock` for the same reason optional beats require and hide beats show:
 * when two rules disagree, the one that lets the person act wins. It also beats
 * Build's own Locked answer, which is what makes "locked for customers, open to
 * our reps" a single rule rather than a condition written twice.
 */
export const ruleUnlockedKeys = (rules: FormRule[], values: Record<string, unknown>) =>
  firingTargets(rules, values, 'unlock');

/** Every key a require/optional/lock action names, whether or not its rule fires. */
export function ruleRequirednessTargetKeys(rules: FormRule[]): Set<string> {
  const keys = new Set<string>();
  for (const r of rules) {
    for (const a of r.actions || []) {
      if (a.type === 'require' || a.type === 'optional') a.targets.forEach((t) => keys.add(t));
    }
  }
  return keys;
}

/** Every key a lock/unlock action names, whether or not its rule fires. */
export function ruleLockTargetKeys(rules: FormRule[]): Set<string> {
  const keys = new Set<string>();
  for (const r of rules) {
    for (const a of r.actions || []) {
      if (a.type === 'lock' || a.type === 'unlock') a.targets.forEach((t) => keys.add(t));
    }
  }
  return keys;
}

/** Every key a `calc` action writes, whether or not its rule fires. */
export function ruleCalcTargetKeys(rules: FormRule[]): Set<string> {
  const keys = new Set<string>();
  for (const r of rules) {
    for (const a of r.actions || []) if (a.type === 'calc' && a.target) keys.add(a.target);
  }
  return keys;
}

// ---- section / card / widget targets ----------------------------------------
// Show/hide actions can target whole containers alongside fields. The targets
// are encoded as 'sec:<sectionId>', 'card:<sectionId>:<cardId>' and
// 'wnode:<scope>' so they travel through the same string lists as field keys.
export const sectionTarget = (sectionId: string) => `sec:${sectionId}`;
export const cardTarget = (sectionId: string, cardId: string) => `card:${sectionId}:${cardId}`;
/** A repeatable container — every instance of it, and its add button. */
export const widgetTarget = (scope: string) => `wnode:${scope}`;
export const isContainerTarget = (t: string) =>
  t.startsWith('sec:') || t.startsWith('card:') || t.startsWith('wnode:');

/**
 * Does a container render right now?
 *
 * One marked hidden in the builder waits for a `show` rule exactly as a hidden
 * field does — it appears only while a rule naming it fires, and never at all
 * without one. One left shown renders unless a rule takes it away. `hidden`
 * already carries both halves of the rule answer: a fired `hide` puts a target
 * in it, and so does a `show` target no rule has fired for yet.
 */
export const containerVisible = (
  target: string, hiddenByDefault: boolean | undefined,
  hidden: Set<string>, showTargeted: Set<string>
): boolean => (hiddenByDefault ? showTargeted.has(target) : true) && !hidden.has(target);

export interface PrefillResult {
  /** values with prefill applied on top (what should render and submit) */
  values: Record<string, unknown>;
  /** target key -> source key, for targets mirrored by a LOCKED action (render disabled) */
  locked: Record<string, string>;
  /** target key -> source key, for targets filled by an EDITABLE action (note only) */
  prefilled: Record<string, string>;
  /**
   * Targets a row wrote a FIXED value into rather than copying an answer.
   *
   * A fixed row has no source field to name, so it is recorded in `locked` /
   * `prefilled` against ITSELF — those two maps are what disables a control,
   * keeps the field out of validation and earns it a note, and all three still
   * want to happen. This set is how the renderer knows to say "set by this
   * form" instead of naming a field that was never involved.
   */
  fixed: Set<string>;
}

/** The field a key belongs to — 'shipAddress.company' is answered as 'shipAddress'. */
const headOf = (key: string) => { const i = key.indexOf('.'); return i < 0 ? key : key.slice(0, i); };

/**
 * Does any rule READ a field some rule WRITES?
 *
 * When nothing a rule reads can be written by a rule, one pass is already the
 * whole answer and settling is a waste — which is most forms. Sub-paths are
 * compared by the field they belong to, so the check errs towards settling
 * rather than towards missing a chain.
 */
function rulesChain(rules: FormRule[]): boolean {
  const written = new Set<string>();
  for (const r of rules) {
    for (const a of r.actions || []) {
      if (a.type === 'prefill') for (const m of a.map || []) if (m.to) written.add(headOf(m.to));
    }
  }
  if (!written.size) return false;
  for (const r of rules) {
    for (const c of r.when || []) if (c.field && written.has(headOf(c.field))) return true;
    for (const a of r.actions || []) {
      if (a.type === 'prefill') for (const m of a.map || []) if (m.from && written.has(headOf(m.from))) return true;
    }
  }
  return false;
}

/** Same answers? Enough to know a settling pass changed nothing. */
function sameValues(a: Record<string, unknown>, b: Record<string, unknown>): boolean {
  const keys = Object.keys(a);
  if (keys.length !== Object.keys(b).length) return false;
  for (const k of keys) {
    const x = a[k];
    const y = b[k];
    if (x === y) continue;
    if (!(k in b)) return false;
    // Lists and composites are compared by content — a pass rebuilds them, so
    // two equal answers are never the same object.
    const both = x && y && typeof x === 'object' && typeof y === 'object';
    if (!both || JSON.stringify(x) !== JSON.stringify(y)) return false;
  }
  return true;
}

/**
 * How many times the answers may be re-settled before the engine stops.
 *
 * A chain needs one pass per link, and a form with eight links of prefill
 * feeding prefill does not exist. The cap is here for the shape that has no
 * answer at all — two rules that each undo the other — where stopping with
 * the last pass's answers is the only honest thing left: it is what the rules
 * say after eight readings, it is the same every time, and it ends.
 */
const SETTLE_PASSES = 8;

/**
 * Apply prefill actions from every rule that fires. `locked` targets mirror
 * their source while the rule holds; `editable` targets mirror only until the
 * user edits them (`touched`).
 *
 * A row carrying `value` writes that literal instead of reading a source. It
 * obeys the same two modes for the same reason — locked rewrites it on every
 * pass, editable stops the moment the respondent touches the field — and the
 * control it writes into is the field's own, so a dropdown stays a dropdown.
 *
 * Targets (and sources) may be composite sub-paths like 'shipAddress.company'.
 * Writing a part re-derives the composite's combined value via `kinds`, so a
 * prefilled company still lands inside fullAddress. Editing ANY part of a
 * composite marks the whole field touched, which ends its editable prefills.
 * A row naming a part beats a row naming the whole field whichever order they
 * are in (see `subWrites`), because one of them is specific and the other is
 * not.
 *
 * ---- Why this runs more than once ----
 *
 * One rule writes what another rule watches, and the Logic page has no order
 * to speak of: rules are listed newest first, nothing says which runs when,
 * and nothing lets anyone change it. A single pass therefore answered a rule
 * about a field that a rule further down the list had not filled in yet — and
 * because a new rule is added to the FRONT, building the rule that feeds and
 * then the rule that reads guaranteed the reader ran first and saw nothing.
 *
 * So the pass is repeated until the answers stop moving. Each pass starts
 * again from the respondent's own answers and re-derives every written value
 * from scratch, using the pass before it only to decide WHICH rules fire; a
 * rule that stops holding therefore takes its value back with it, exactly as
 * it always did. When a pass reproduces what it was given, the rules and the
 * answers agree and that is the result.
 */
export function applyPrefill(
  rules: FormRule[], values: Record<string, unknown>, touched: Record<string, boolean>,
  kinds: CompositeKinds = {}
): PrefillResult {
  let result = prefillPass(rules, values, values, touched, kinds);
  if (!rulesChain(rules)) return result;
  let seen = values;
  for (let pass = 1; pass < SETTLE_PASSES && !sameValues(result.values, seen); pass += 1) {
    seen = result.values;
    result = prefillPass(rules, values, seen, touched, kinds);
  }
  return result;
}

/**
 * One reading of every rule.
 *
 * `values` are the answers the respondent gave — what the written values are
 * rebuilt on top of. `seen` is what the pass before this one settled on, and
 * it is what conditions are weighed against, updated as this pass writes so a
 * rule can still feed the rule after it without waiting for another pass.
 */
function prefillPass(
  rules: FormRule[], values: Record<string, unknown>, seen: Record<string, unknown>,
  touched: Record<string, boolean>, kinds: CompositeKinds
): PrefillResult {
  const out = { ...values };
  const view = { ...seen };
  const locked: Record<string, string> = {};
  const prefilled: Record<string, string> = {};
  const fixed = new Set<string>();
  /**
   * Composite key -> the sub-paths THIS pass has written into it.
   *
   * A whole-field write replaces the composite outright, which is right when
   * what it replaces is the respondent's own answer and wrong when it is a
   * sub-path row of the same prefill: "copy Company Name into Shipping
   * Address › Company" followed by "copy Company Address into Shipping
   * Address" silently threw the first row away, and the rows after it
   * survived only because they merged on top of the replacement. A sub-path
   * names one part and the whole-field row names the rest, so the specific
   * row wins over the general one however they are ordered — the Logic page
   * gives no way to reorder them, and nothing on screen says the order
   * matters.
   */
  const subWrites: Record<string, Record<string, unknown>> = {};
  const normalize = (head: string, merged: Record<string, unknown>) => {
    const kind = kinds[head];
    return kind === 'name' ? normalizeNameValue(merged)
      : kind ? normalizeAddressValue(merged, { shipping: kind === 'shipping' })
      : merged;
  };
  const asObject = (v: unknown) =>
    (v && typeof v === 'object' && !Array.isArray(v) ? v as Record<string, unknown> : null);
  const write = (to: string, v: unknown) => {
    const i = to.indexOf('.');
    if (i < 0) {
      // Sub-paths already written into this composite are re-applied over the
      // incoming value. Only a composite can carry them, so a whole-field
      // write of a scalar (or into a field no sub-path row has touched) is
      // assigned exactly as it always was.
      const kept = subWrites[to];
      const base = kept && asObject(v);
      out[to] = base ? normalize(to, { ...base, ...kept }) : v;
      view[to] = out[to];
      return;
    }
    const head = to.slice(0, i);
    const path = to.slice(i + 1);
    (subWrites[head] ||= {})[path] = v;
    out[head] = normalize(head, { ...(asObject(out[head]) || {}), [path]: v });
    view[head] = out[head];
  };
  const wasTouched = (to: string) => !!touched[to] || (to.includes('.') && !!touched[to.slice(0, to.indexOf('.'))]);
  for (const r of rules) {
    if (!ruleFires(r, view)) continue;
    for (const a of r.actions || []) {
      if (a.type !== 'prefill') continue;
      for (const m of a.map || []) {
        // A literal is written as it stands; a source is read off the best
        // answer there is for it — what this pass has built up, over what the
        // pass before settled on — so one prefill can feed the next.
        const isFixed = m.value !== undefined;
        const source = isFixed ? m.to : (m.from || '');
        if (!source || !m.to) continue;
        const v = isFixed ? m.value : valueAt(view, source) ?? '';
        if (a.mode === 'locked') { write(m.to, v); locked[m.to] = source; }
        else if (!wasTouched(m.to)) { write(m.to, v); prefilled[m.to] = source; }
        else continue;
        if (isFixed) fixed.add(m.to);
      }
    }
  }
  return { values: out, locked, prefilled, fixed };
}

/**
 * Evaluate every `calc` action on the form. `counts` maps widget-node scopes to
 * instance counts in the current context. `sumOver` (supplied by evaluate.ts)
 * collects a field's values across every instance of a scope so `sum` works for
 * real.
 *
 * A rule that does NOT fire CLEARS its target rather than leaving the last
 * number it computed: a stale total sitting in a read-only field the respondent
 * cannot correct is worse than an empty one. An upgraded calculation carries
 * `trigger: 'always'`, so it computes on every pass exactly as it always did.
 *
 * That clearing is counted per TARGET, not per rule — the same reading SHOW
 * gets in ruleHiddenKeys, and for the same reason. Two rules writing one total
 * on different answers ("add the points when Yes" beside "add them when No")
 * is how a scorecard is written, and clearing per rule let whichever one did
 * not fire blank the total the one that did had just computed. A target goes
 * empty only while NONE of the rules writing it fires; among those that do,
 * the last one still wins.
 *
 * ---- This runs ONCE, unlike applyPrefill, and must keep doing so ----
 *
 * A calculation reads the ANSWER in a field, never the total it last put
 * there: `total = total + availablePoints` is an ordinary scorecard row, and
 * every reading of it has to see the answer `total` started with. Settle this
 * the way the answers are settled and that row stops being a sum and becomes
 * an accumulator, multiplying every scorecard on every form by however many
 * passes it took to give up. So a calculation still cannot read another
 * calculation's result — `a = b + 1` where `b` is itself computed reads b as
 * empty — and fixing THAT means telling a calc's own target apart from every
 * other input, not repeating the pass.
 */
export function computeCalcs(
  rules: FormRule[],
  values: Record<string, unknown>,
  counts: Record<string, number>,
  sumOver?: (field: string, ofScope: string) => number,
  /** scorecard totals for a section, or the whole form - also supplied by evaluate.ts */
  scoreOver?: (section?: string) => ScoreTotals
): Record<string, string | number> {
  const num = (v: unknown) => { const n = parseFloat(String(v ?? '')); return Number.isFinite(n) ? n : 0; };
  const out: Record<string, string | number> = {};
  /** every target a calc names -> did any rule writing it fire? */
  const written = new Map<string, boolean>();
  for (const r of rules) {
    const fires = ruleFires(r, values);
    for (const a of r.actions || []) {
      if (a.type !== 'calc' || !a.target) continue;
      written.set(a.target, written.get(a.target) || fires);
      if (!fires) continue;
      const c = a.calc;
      if (c.type === 'count') out[a.target] = counts[c.of] ?? 0;
      else if (c.type === 'sum') out[a.target] = sumOver ? sumOver(c.field, c.of) : 0;
      else if (c.type === 'math') {
        const x = num(valueAt(values, c.a)), y = num(valueAt(values, c.b));
        out[a.target] = c.op === '+' ? x + y : c.op === '-' ? x - y : x * y;
      } else if (c.type === 'join') {
        out[a.target] = c.fields.map((k) => String(valueAt(values, k) ?? '')).filter(Boolean).join(c.sep ?? ' ');
      } else if (c.type === 'fixed') out[a.target] = c.value ?? '';
      else if (c.type === 'score') {
        const t = scoreOver ? scoreOver(c.section) : NO_SCORE;
        out[a.target] = c.mode === 'earned' ? t.earned : c.mode === 'available' ? t.available : scorePercent(t);
      }
    }
  }
  for (const [target, fired] of written) if (!fired) out[target] = '';
  return out;
}

/** Plain-English description of what a calc produced (for the read-only note). */
export function calcNote(rules: FormRule[], target: string): string {
  for (const r of rules) {
    for (const a of r.actions || []) {
      if (a.type !== 'calc' || a.target !== target) continue;
      const c = a.calc;
      const what = c.type === 'count' ? `count of ${c.of}s`
        : c.type === 'sum' ? `sum of ${c.field}`
        : c.type === 'math' ? 'computed'
        : c.type === 'fixed' ? 'a fixed value'
        : c.type === 'score' ? (c.mode === 'earned' ? 'the points these answers earned'
          : c.mode === 'available' ? 'the points these questions had to give'
          : 'the share of the available points earned')
        : 'joined text';
      // A conditional calculation is empty while its rule does not hold, and
      // saying so is the difference between "blank" and "broken".
      return r.trigger === 'always' ? what : `${what}, while this form's rule holds`;
    }
  }
  return '';
}

/** All the rules that touch a field — as a condition, an action target, a
 *  prefill source, or a calculation's input or output. A composite counts as
 *  touched when any of its sub-paths is named. */
export function rulesForField(rules: FormRule[], key: string): FormRule[] {
  const hits = (k: string) => k === key || k.startsWith(`${key}.`);
  return rules.filter((r) => {
    if ((r.when || []).some((c) => hits(c.field))) return true;
    return (r.actions || []).some((a) => {
      if (a.type === 'prefill') return (a.map || []).some((m) => hits(m.from || '') || hits(m.to));
      if (a.type === 'calc') {
        return hits(a.target)
          || (a.calc.type === 'sum' && hits(a.calc.field))
          || (a.calc.type === 'math' && (hits(a.calc.a) || hits(a.calc.b)))
          || (a.calc.type === 'join' && a.calc.fields.some(hits));
      }
      if (a.type === 'goto') return false; // names a page, not a field
      return (a.targets || []).includes(key);
    });
  });
}

/** Every field key a rule involves (for canvas highlighting). Sub-paths report
 *  their composite's key so the whole field highlights. */
export function ruleKeys(r: FormRule): string[] {
  const ks: string[] = [];
  const push = (k: string) => { const i = k.indexOf('.'); ks.push(i < 0 ? k : k.slice(0, i)); };
  (r.when || []).forEach((c) => push(c.field));
  for (const a of r.actions || []) {
    if (a.type === 'prefill') (a.map || []).forEach((m) => { if (m.from) push(m.from); push(m.to); });
    else if (a.type === 'calc') {
      push(a.target);
      if (a.calc.type === 'sum') push(a.calc.field);
      if (a.calc.type === 'math') { push(a.calc.a); push(a.calc.b); }
      if (a.calc.type === 'join') a.calc.fields.forEach(push);
    } else if (a.type === 'goto') { /* a page, not a field */ }
    else ks.push(...(a.targets || []));
  }
  return ks;
}

/** One-line summary of an action, for chips and rule sentences. */
export function actionText(
  a: RuleAction, lbl: (k: string) => string, pageLbl: (id: string) => string = (id) => id,
  sectionLbl: (id: string) => string = (id) => id
): string {
  if (a.type === 'goto') return `skip to ${a.page ? pageLbl(a.page) : '(no page yet)'}`;
  if (a.type === 'prefill') {
    const n = (a.map || []).length;
    return `copy ${n} value${n === 1 ? '' : 's'} (${a.mode === 'locked' ? 'locked mirror' : 'editable prefill'})`;
  }
  if (a.type === 'calc') {
    const c = a.calc;
    const rhs = c.type === 'count' ? `count of ${c.of}s`
      : c.type === 'sum' ? `sum of ${lbl(c.field)} across ${c.of}s`
      : c.type === 'math' ? `${lbl(c.a)} ${c.op === '*' ? '\u00d7' : c.op} ${lbl(c.b)}`
      : c.type === 'fixed' ? `"${c.value ?? ''}"`
      : c.type === 'score' ? `${c.mode === 'earned' ? 'score earned' : c.mode === 'available' ? 'score available' : 'score %'} ${c.section ? `in ${sectionLbl(c.section)}` : 'across the form'}`
      : 'joined text';
    return `set ${lbl(a.target)} = ${rhs}`;
  }
  const verb = a.type === 'require' ? 'make required'
    : a.type === 'optional' ? 'make optional'
    : a.type;
  const t = (a.targets || []).map(lbl);
  const list = t.slice(0, 3).join(', ') + (t.length > 3 ? ` +${t.length - 3} more` : '');
  return `${verb} ${list || '(nothing yet)'}`;
}
